// ===================================
// BOOK SESSION PAGE - STAGE B FIX
// src/pages/BookSession.js
// Stage 11 Fix: Fetch pricing from API, better error handling
// Stage B Fix: Resume required for certain categories, removed duplicate company name
// Phase 5: Coupon/Discount code support
// Only 3 active categories: HR Interview, Job-Specific, Academy Guidance
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './BookSession.css';

// Lazy load optional components
let ResumeSelector, JobDescriptionInput;
try {
  ResumeSelector = require('../components/ResumeSelector').default;
  JobDescriptionInput = require('../components/JobDescriptionInput').default;
} catch (e) {
  // Components not available
}

const BookSession = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // PRICING - Loaded from API
  const [pricing, setPricing] = useState({
    text: { 15: 9.99, 30: 14.99, 60: 24.99 },
    voice: { 15: 14.99, 30: 24.99, 60: 39.99 },
    phone: { 15: 19.99, 30: 34.99, 60: 49.99 }
  });
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    category_id: null,
    category_slug: null,
    subcategory_id: null,
    subcategory_slug: null,
    resume_id: null,
    resume: null,
    job_description_id: null,
    job_description_text: '',
    company_name: '',
    interview_type: 'voice',
    duration: 30,
    interviewer_personality: 'friendly',
    experience_level: 'intermediate',
    realtime_feedback: false,
    recording_consent: false,
    recommendation_preference: 'open'  // <-- ADD THIS LINE
  });

  // Default categories fallback - ONLY 3 ACTIVE CATEGORIES
  const defaultCategories = [
    {
      id: 'default-hr',
      slug: 'hr-interview',
      name: 'HR Interview',
      description: 'Practice common HR and behavioral interview questions',
      icon: '💼',
      requires_resume: false,
      requires_job_description: false,
      requires_company_name: false
    },
    {
      id: 'default-job-specific',
      slug: 'job-specific',
      name: 'Job-Specific Interview',
      description: 'Practice for a specific job with your resume and job description',
      icon: '🎯',
      requires_resume: true,
      requires_job_description: true,
      requires_company_name: true
    },
    {
      id: 'default-academy',
      slug: 'academy-guidance',
      name: 'Academy Program Guidance',
      description: 'Get personalized program recommendations based on your goals',
      icon: '🎓',
      requires_resume: true,
      requires_job_description: false,
      requires_company_name: false
    }
    // FUTURE CATEGORIES (uncomment when ready)
    /*
    {
      id: 'default-technical',
      slug: 'technical',
      name: 'Technical Interview',
      description: 'Practice technical and problem-solving questions',
      icon: '💻',
      requires_resume: true,
      requires_job_description: true,
      requires_company_name: false
    },
    {
      id: 'default-behavioral',
      slug: 'behavioral',
      name: 'Behavioral Interview',
      description: 'Practice STAR method responses',
      icon: '🎯',
      requires_resume: false,
      requires_job_description: false,
      requires_company_name: false
    }
    */
  ];

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setError('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  // LOAD PRICING FROM API
  useEffect(() => {
    const loadPricing = async () => {
      try {
        setLoadingPricing(true);
        const response = await api.get('/config/pricing');
        
        if (response.data) {
          const pricingData = response.data.pricing || response.data;
          
          // Convert to numbers
          const converted = {};
          for (const [type, durations] of Object.entries(pricingData)) {
            converted[type] = {};
            for (const [dur, price] of Object.entries(durations)) {
              converted[type][dur] = parseFloat(price) || 0;
            }
          }
          
          setPricing(converted);
          console.log('✅ Pricing loaded:', converted);
        }
      } catch (err) {
        console.warn('Using default pricing:', err.message);
      } finally {
        setLoadingPricing(false);
      }
    };
    loadPricing();
  }, []);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await api.get('/categories');
        const cats = response.data?.categories || response.data || [];
        
        if (cats.length > 0) {
          setCategories(cats);
        } else {
          setCategories(defaultCategories);
        }
      } catch (err) {
        console.warn('Using default categories:', err.message);
        setCategories(defaultCategories);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Load subcategories
  useEffect(() => {
    const loadSubcategories = async () => {
      if (!formData.category_slug) {
        setSubcategories([]);
        return;
      }
      
      try {
        const response = await api.get(`/categories/${formData.category_slug}/subcategories`);
        setSubcategories(response.data?.subcategories || []);
      } catch (err) {
        setSubcategories([]);
      }
    };
    loadSubcategories();
  }, [formData.category_slug]);

  // Clear coupon when pricing factors change
  useEffect(() => {
    if (couponData) {
      setCouponData(null);
      setCouponError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.interview_type, formData.duration]);

  // Computed values
  const selectedCategory = categories.find(c => c.id === formData.category_id);
  const currentPrice = pricing[formData.interview_type]?.[formData.duration] || 0;

  // Calculate final price after discount
  const originalPriceInCents = Math.round((currentPrice || 0) * 100);
  const discountAmountInCents = couponData?.discount_amount || 0;
  const finalPriceInCents = couponData?.final_amount ?? originalPriceInCents;
  const finalPrice = finalPriceInCents / 100;

  // STAGE B FIX: Updated step logic - Resume shows when required OR component exists
  // const getSteps = () => {
  //   const steps = ['Category'];
  //   if (subcategories.length > 0) steps.push('Focus Area');
  //   // Show resume step if category requires it OR if component is available
  //   if (selectedCategory?.requires_resume || ResumeSelector) steps.push('Resume');
  //   if (selectedCategory?.requires_job_description && JobDescriptionInput) steps.push('Job Details');
  //   steps.push('Format', 'Duration', 'Style', 'Review');
  //   return steps;
  // };

  const getSteps = () => {
    const steps = ['Category'];
    if (subcategories.length > 0) steps.push('Focus Area');
    if (selectedCategory?.requires_resume || ResumeSelector) steps.push('Resume');
    if (selectedCategory?.requires_job_description && JobDescriptionInput) steps.push('Job Details');
    // NEW: Add recommendation preference step for academy-guidance
    if (formData.category_slug === 'academy-guidance') steps.push('Recommendations');
    steps.push('Format', 'Duration', 'Style', 'Review');
    return steps;
  };

  const steps = getSteps();
  const totalSteps = steps.length;

  // // Handlers
  // const handleCategorySelect = (category) => {
  //   setFormData({
  //     ...formData,
  //     category_id: category.id,
  //     category_slug: category.slug,
  //     subcategory_id: null,
  //     subcategory_slug: null,
  //     // Reset resume/JD when category changes
  //     resume_id: null,
  //     resume: null,
  //     job_description_id: null,
  //     job_description_text: '',
  //     company_name: '',
  //     recommendation_preference: 'open'  // <-- ADD THIS LINE (reset on category change)
  //   });
  // };

const handleCategorySelect = (category) => {
  setFormData(prev => ({
    ...prev,
    category_id: category.id,
    category_slug: category.slug,
    subcategory_id: null,
    subcategory_slug: null,
    resume_id: null,
    resume: null,
    job_description_id: null,
    job_description_text: '',
    company_name: '',
    recommendation_preference: 'open'
  }));
  
  // Clear any loaded subcategories from previous selection
  setSubcategories([]);
  
  setStep(prev => prev + 1);
};

  const handleSubcategorySelect = (sub) => {
    setFormData({
      ...formData,
      subcategory_id: sub.id,
      subcategory_slug: sub.slug
    });
  };

  const handleResumeSelect = useCallback((resume) => {
    setFormData(prev => ({
      ...prev,
      resume_id: resume?.id || null,
      resume: resume || null
    }));
  }, []);

  // STAGE B FIX: Updated to receive companyName from JobDescriptionInput
  const handleJobDescriptionChange = useCallback((data) => {
    setFormData(prev => ({
      ...prev,
      job_description_id: data.documentId || data.id || null,
      job_description_text: data.text || '',
      company_name: data.companyName || prev.company_name
    }));
  }, []);

  /**
   * Apply coupon code and get discount
   */
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError(null);

    try {
      // Convert price to cents for API
      const amountInCents = Math.round(currentPrice * 100);
      
      const response = await api.post('/coupons/validate', {
        code: couponCode.trim(),
        amount: amountInCents
      });

      if (response.data.valid) {
        setCouponData(response.data);
        setCouponError(null);
        console.log('✅ Coupon applied:', response.data);
      } else {
        setCouponData(null);
        setCouponError(response.data.message || 'Invalid coupon code');
      }
    } catch (err) {
      console.error('Coupon validation error:', err);
      setCouponData(null);
      setCouponError(err.response?.data?.message || 'Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  /**
   * Remove applied coupon
   */
  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponData(null);
    setCouponError(null);
  };

  // STAGE B FIX: Updated validation - resume required when category requires it
  const validateStep = () => {
    const currentStepName = steps[step - 1];
    
    switch (currentStepName) {
      case 'Category': return !!formData.category_id;
      case 'Focus Area': return !!formData.subcategory_id;
      case 'Resume':
        // STAGE B FIX: Resume required when category requires it
        if (selectedCategory?.requires_resume) {
          return !!formData.resume_id;
        }
        return true;  // Optional for other categories
      case 'Job Details':
        const hasJD = !!formData.job_description_id || formData.job_description_text.length > 50;
        const hasCompany = !selectedCategory?.requires_company_name || formData.company_name.trim().length > 0;
        return hasJD && hasCompany;
      case 'Format': return !!formData.interview_type;
      case 'Duration': return !!formData.duration;
      case 'Recommendations': return !!formData.recommendation_preference;
      case 'Style': return true;
      default: return true;
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/payments/create-checkout-session', {
        interview_type: formData.interview_type,
        duration: formData.duration,
        interviewer_personality: formData.interviewer_personality,
        experience_level: formData.experience_level,
        recording_consent: formData.recording_consent,
        category_id: formData.category_id?.toString().startsWith('default-') ? null : formData.category_id,
        category_slug: formData.category_slug,
        subcategory_id: formData.subcategory_id,
        subcategory_slug: formData.subcategory_slug,
        company_name: formData.company_name,
        resume_id: formData.resume_id,
        job_description_id: formData.job_description_id,
        job_description_text: formData.job_description_text,
        realtime_feedback: formData.realtime_feedback,
        // Include coupon code if applied
        coupon_code: couponData ? couponCode.trim() : null,
        recommendation_preference: formData.recommendation_preference
      });
      
      // Real Stripe payment - redirect to Stripe
      if (response.data.url) {
        window.location.href = response.data.url;
      } 
      // Simulated payment - redirect to success page with session info
      else if (response.data.sessionId) {
        navigate(`/app/payment-success?session_id=${response.data.sessionId}&simulated=true`);
      }
    } catch (err) {
      // Handle coupon-specific errors
      if (err.response?.data?.coupon_error) {
        setCouponError(err.response.data.message);
        setCouponData(null);
      } else {
        setError(err.response?.data?.message || 'Failed to create checkout session');
      }
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep() && step < totalSteps) setStep(step + 1);
  };

const prevStep = () => {
  if (step > 1) {
    // If going back to category step, reset category selection
    if (step === 2) { 
      setFormData(prev => ({
        ...prev,
        category_id: null,
        category_slug: null,
        subcategory_id: null,
        subcategory_slug: null
      }));
      setSubcategories([]);
    }
    setStep(step - 1);
  }
};

  const formatPrice = (price) => {
    const num = typeof price === 'number' ? price : parseFloat(price) || 0;
    return `$${num.toFixed(2)}`;
  };

  // Render step content
  const renderStepContent = () => {
    const currentStepName = steps[step - 1];

    switch (currentStepName) {
      // case 'Category':
      //   return (
      //     <div className="step-content">
      //       <h2>Choose Interview Type</h2>
      //       <p className="step-description">Select the type of service or interview practice you want</p><br/>
            
      //       {loadingCategories ? (
      //         <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
      //       ) : (
      //         <div className="options-grid category-grid">
      //           {categories.map(cat => (
      //             <div
      //               key={cat.id}
      //               className={`option-card category-card ${formData.category_id === cat.id ? 'selected' : ''}`}
      //               onClick={() => handleCategorySelect(cat)}
      //             >
      //               <span className="option-icon">{cat.icon || '📋'}</span>
      //               <h3>{cat.name}</h3>
      //               <p>{cat.description}</p>
      //               {/* STAGE B: Show requirements badges */}
      //               {(cat.requires_resume || cat.requires_job_description) && (
      //                 <div className="category-requirements">
      //                   {cat.requires_resume && <span className="req-badge">📄 Resume</span>}
      //                   {cat.requires_job_description && <span className="req-badge">📋 JD</span>}
      //                 </div>
      //               )}
      //             </div>
      //           ))}
      //         </div>
      //       )}
      //     </div>
      //   );

    // ===================================
// PREMIUM CATEGORY SECTION - BookSession.js
// Replace the 'Category' case in renderStepContent()
// Coming Soon badge at bottom, consistent alignment
// ===================================

case 'Category':
  // All categories with premium descriptions
  const allCategoryData = [
    // ACTIVE CATEGORIES
    {
      id: categories.find(c => c.slug === 'hr-interview')?.id || 'default-hr',
      slug: 'hr-interview',
      name: 'HR Interview',
      tagline: 'Ace your next interview',
      description: 'Master behavioral questions, nail your "Tell me about yourself," and Situational Interviews using STAR method.  Get real-time feedback that transforms nervous rambling into confident, structured answers.',
      icon: '💼',
      tags: ['Behavioral', 'Situational', 'Cultural Fit', 'STAR Method'],
      requires_resume: false,
      requires_job_description: false,
      comingSoon: false
    },
    {
      id: categories.find(c => c.slug === 'job-specific')?.id || 'default-job-specific',
      slug: 'job-specific',
      name: 'Job-Specific Interview',
      tagline: 'Tailored to your dream role',
      description: 'Upload the job posting and your resume — our AI analyzes both to ask the exact questions you\'ll face. Practice like you\'re already in the room.',
      icon: '🎯',
      tags: ['Resume Match', 'Role-Specific', 'Company-Tailored'],
      requires_resume: true,
      requires_job_description: true,
      comingSoon: false
    },
    {
      id: categories.find(c => c.slug === 'academy-guidance')?.id || 'default-academy',
      slug: 'academy-guidance',
      name: 'Academy Guidance',
      tagline: 'Find your perfect path',
      description: 'Not sure what to study or where? Get personalized recommendations for universities, bootcamps, and certifications aligned with your career goals.',
      icon: '🎓',
      tags: ['Career Path', 'Program Match', 'Personalized'],
      requires_resume: true,
      requires_job_description: false,
      comingSoon: false
    },
    // COMING SOON CATEGORIES
    {
      id: 'coming-soon-resume',
      slug: 'resume-builder',
      name: 'Resume Critique & Builder',
      tagline: 'Your resume, perfected',
      description: 'AI-powered resume analysis with ATS optimization. Get instant feedback, keyword suggestions, and one-click improvements. Never struggle with formatting again.',
      icon: '📄',
      tags: ['ATS-Optimized', 'AI Feedback', 'One-Click Fix'],
      comingSoon: true
    },
    {
      id: 'coming-soon-clarity',
      slug: 'clarity-session',
      name: 'CLARITY Session',
      tagline: 'Your AI career mentor',
      description: 'Feeling stuck? Work with an AI mentor to define your goals, create actionable milestones, and build a roadmap to the career you actually want.',
      icon: '🧭',
      tags: ['Goal Setting', 'Milestones', 'Action Plans'],
      comingSoon: true
    },
    {
      id: 'coming-soon-university',
      slug: 'university-admission',
      name: 'University Admissions',
      tagline: 'Stand out from thousands',
      description: 'Prep for MBA, Law, Med School, and more. Practice with realistic admission interview questions and get feedback that helps you shine.',
      icon: '🏛️',
      tags: ['MBA', 'Law School', 'Med School', 'Grad School'],
      comingSoon: true
    }
  ];

  return (
    <div className="step-content">
      <h2>What would you like to work on?</h2>
      <p className="step-description">Choose your session type to get started</p>
      
      {loadingCategories ? (
        <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
      ) : (
        <div className="premium-category-grid">
          {allCategoryData.map(cat => (
            <div
              key={cat.id}
              className={`premium-category-card ${formData.category_id === cat.id ? 'selected' : ''} ${cat.comingSoon ? 'coming-soon' : ''}`}
              onClick={() => !cat.comingSoon && handleCategorySelect(cat)}
            >
              {/* Header: Icon + Title */}
              <div className="premium-card-header">
                <div className="premium-icon-box">
                  <span>{cat.icon}</span>
                </div>
                <div className="premium-title-group">
                  <h3>{cat.name}</h3>
                  <span className="premium-tagline">{cat.tagline}</span>
                </div>
              </div>
              
              {/* Description - Fixed Height */}
              <p className="premium-description">{cat.description}</p>
              
              {/* Tags - Fixed Height */}
              <div className="premium-tags">
                {cat.tags.map((tag, idx) => (
                  <span key={idx} className="premium-tag">{tag}</span>
                ))}
              </div>
              
              {/* Footer: Requirements or Coming Soon */}
              <div className="premium-card-footer">
                {!cat.comingSoon ? (
                  <>
                    {/* Requirements Section - Fixed Height */}
                    <div className="premium-requirements">
                      {cat.requires_resume && <span className="premium-req">📄 Resume</span>}
                      {cat.requires_job_description && <span className="premium-req">📋 Job Description</span>}
                      {!cat.requires_resume && !cat.requires_job_description && (
                        <span className="premium-req quick-start">⚡ Quick Start</span>
                      )}
                    </div>
                    <div className={`premium-select-btn ${formData.category_id === cat.id ? 'selected' : ''}`}>
                      {formData.category_id === cat.id ? (
                        <span className="check-icon">✓</span>
                      ) : (
                        <span className="arrow-icon">→</span>
                      )}
                    </div>
                  </>
                ) : (
                  /* Coming Soon Badge - Centered at Bottom */
                  <div className="premium-coming-soon-footer">
                    <span className="coming-soon-badge-bottom">Coming Soon</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  case 'Focus Area':
        return (
          <div className="step-content">
            <h2>Select Focus Area</h2>
            <p className="step-description">Choose a specific area</p> <br/>
            <div className="options-grid">
              {subcategories.map(sub => (
                <div
                  key={sub.id}
                  className={`option-card ${formData.subcategory_id === sub.id ? 'selected' : ''}`}
                  onClick={() => handleSubcategorySelect(sub)}
                >
                  <span className="option-icon">{sub.icon || '🎯'}</span>
                  <h3>{sub.name}</h3>
                  <p>{sub.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      // STAGE B FIX: Updated Resume step with Required/Optional badge
      case 'Resume':
        const isResumeRequired = selectedCategory?.requires_resume;
        return (
          <div className="step-content">
            <h2>
              Select Your Resume{' '}
              <span className={isResumeRequired ? 'required-badge' : 'optional-badge'}>
                ({isResumeRequired ? 'Required' : 'Optional'})
              </span>
            </h2>
            <p className="step-description">
              {isResumeRequired 
                ? 'Your resume is required for personalized interview questions'
                : 'Add a resume for personalized interview questions based on your experience'
              }
            </p><br/>
            {ResumeSelector ? (
              <ResumeSelector selectedResumeId={formData.resume_id} onSelect={handleResumeSelect} />
            ) : (
              <div className="no-resume-note">
                <p>Resume upload is not available. You can continue without a resume.</p>
              </div>
            )}
            {/* Only show skip button if resume is optional */}
            {!isResumeRequired && (
              <button 
                className="btn btn-link skip-btn" 
                onClick={() => {
                  setFormData(prev => ({ ...prev, resume_id: null, resume: null }));
                  nextStep();
                }}
              >
                Skip this step →
              </button>
            )}
            {/* Show warning if required but not selected */}
            {isResumeRequired && !formData.resume_id && (
              <div className="validation-warning">
                ⚠️ Please select a resume to continue
              </div>
            )}
          </div>
        );

      // STAGE B FIX: Removed duplicate company name - JobDescriptionInput handles it
      case 'Job Details':
        return (
          <div className="step-content">
            <h2>Job Details</h2>
            <p className="step-description">Provide details about the position</p>
            {JobDescriptionInput && (
              <JobDescriptionInput
                value={formData.job_description_text}
                documentId={formData.job_description_id}
                companyName={formData.company_name}
                requireCompanyName={selectedCategory?.requires_company_name}
                onChange={handleJobDescriptionChange}
              />
            )}
          </div>
        );

           case 'Recommendations':
        return (
          <div className="step-content">
            <h2>How would you like your recommendations?</h2>
            <p className="step-description">
              Choose whether you want recommendations from our internal academy programs 
              or broader industry-wide suggestions
            </p>
            
            <div className="options-grid recommendation-grid">
              {/* Internal Programs Option */}
              <div
                className={`option-card recommendation-card ${formData.recommendation_preference === 'internal' ? 'selected' : ''}`}
                onClick={() => setFormData({ ...formData, recommendation_preference: 'internal' })}
              >
                <div className="recommendation-icon-wrapper internal">
                  <span className="option-icon">🏠</span>
                </div>
                <h3>Academy Programs</h3>
                <p className="recommendation-description">
                  Get personalized recommendations from our curated in-house courses, 
                  certifications, and career development programs
                </p>
                <div className="recommendation-benefits">
                  <span className="benefit-tag">✓ Verified Curriculum</span>
                  <span className="benefit-tag">✓ Direct Enrollment</span>
                  <span className="benefit-tag">✓ Academy Support</span>
                </div>
                {formData.recommendation_preference === 'internal' && (
                  <div className="selected-indicator">
                    <span className="check-icon">✓</span> Selected
                  </div>
                )}
              </div>
              
              {/* Open Recommendations Option */}
              <div
                className={`option-card recommendation-card ${formData.recommendation_preference === 'open' ? 'selected' : ''}`}
                onClick={() => setFormData({ ...formData, recommendation_preference: 'open' })}
              >
                <div className="recommendation-icon-wrapper open">
                  <span className="option-icon">🌐</span>
                </div>
                <h3>Open Recommendations</h3>
                <p className="recommendation-description">
                  Receive broader recommendations including external universities, 
                  bootcamps, online platforms, and industry certifications worldwide
                </p>
                <div className="recommendation-benefits">
                  <span className="benefit-tag">✓ Wider Selection</span>
                  <span className="benefit-tag">✓ Industry-Wide</span>
                  <span className="benefit-tag">✓ Global Options</span>
                </div>
                {formData.recommendation_preference === 'open' && (
                  <div className="selected-indicator">
                    <span className="check-icon">✓</span> Selected
                  </div>
                )}
              </div>
            </div>
            
            {/* Info note */}
            <div className="recommendation-info-note">
              <span className="info-icon">💡</span>
              <p>
                {formData.recommendation_preference === 'internal' 
                  ? 'Your AI coach will recommend programs exclusively from our academy catalog, ensuring you get guidance on programs we can directly support you with.'
                  : 'Your AI coach will provide recommendations from the broader education market, including universities, online platforms, and industry certifications.'
                }
              </p>
            </div>
          </div>
        );

case 'Format':
  return (
    <div className="step-content">
      <h2>Choose Format</h2>
      <p className="step-description">How would you like to practice?</p><br/>
      <div className="options-grid">
        {[
          // { id: 'text', icon: '💬', name: 'Text Chat', desc: 'Type your responses' },
          { id: 'voice', icon: '🎙️', name: 'Web Voice', desc: 'Speak in your browser' },
          { id: 'phone', icon: '📞', name: 'Phone Call', desc: 'Real phone interview' },
          { id: 'video', icon: '🎥', name: 'Video Call', desc: 'Face-to-face with AI', comingSoon: true }
        ].map(type => (
          <div
            key={type.id}
            className={`option-card ${formData.interview_type === type.id ? 'selected' : ''} ${type.comingSoon ? 'coming-soon' : ''}`}
            onClick={() => !type.comingSoon && setFormData({ ...formData, interview_type: type.id })}
          >
            <span className="option-icon">{type.icon}</span>
            <h3>{type.name}</h3>
            <p>{type.desc}</p>
            {type.comingSoon ? (
              <span className="coming-soon-badge">Coming Soon</span>
            ) : (
              <span className="price-badge">From {formatPrice(pricing[type.id]?.[15])}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
      case 'Duration':
        return (
          <div className="step-content">
            <h2>Select Duration</h2>
            <p className="step-description">How long would you like to practice?</p> <br/>
            <div className="options-grid duration-grid">
              {[15, 30, 60].map(mins => (
                <div
                  key={mins}
                  className={`option-card duration-card ${formData.duration === mins ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, duration: mins })}
                >
                  <span className="duration-time">{mins}</span>
                  <span className="duration-label">minutes</span>
                  <span className="duration-price">{formatPrice(pricing[formData.interview_type]?.[mins])}</span>
                  <span className="duration-desc">
                    {mins === 15 ? 'Quick' : mins === 30 ? 'Standard' : 'Deep dive'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'Style':
        return (
          <div className="step-content">
            <h2>Customize Your Interview</h2>
            <p className="step-description">Fine-tune the experience</p>
            
            <div className="customize-section">
              <h3>Interviewer Style</h3>
              <div className="options-grid small-grid">
                {[
                  { id: 'friendly', icon: '😊', name: 'Friendly', desc: 'Warm and encouraging' },
                  { id: 'strict', icon: '👔', name: 'Strict', desc: 'Professional' },
                  { id: 'technical', icon: '🔬', name: 'Technical', desc: 'Detail-oriented' }
                ].map(style => (
                  <div
                    key={style.id}
                    className={`option-card small ${formData.interviewer_personality === style.id ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, interviewer_personality: style.id })}
                  >
                    <span className="option-icon">{style.icon}</span>
                    <h4>{style.name}</h4>
                    <p>{style.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="customize-section">
              <h3>Experience Level</h3>
              <div className="options-grid small-grid">
                {[
                  { id: 'entry', icon: '🌱', name: 'Entry', desc: '0-2 years' },
                  { id: 'intermediate', icon: '🌿', name: 'Mid', desc: '2-5 years' },
                  { id: 'senior', icon: '🌳', name: 'Senior', desc: '5+ years' }
                ].map(level => (
                  <div
                    key={level.id}
                    className={`option-card small ${formData.experience_level === level.id ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, experience_level: level.id })}
                  >
                    <span className="option-icon">{level.icon}</span>
                    <h4>{level.name}</h4>
                    <p>{level.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'Review':
        return (
          <div className="step-content">
            <h2>Review & Book</h2>
            <p className="step-description">Confirm your session details</p> <br/>
            
            <div className="review-card">
              <div className="review-section">
                <h3>Interview Details</h3>
                <div className="review-grid">
                  <div className="review-item">
                    <span className="review-label">Category</span>
                    <span className="review-value">{selectedCategory?.icon} {selectedCategory?.name}</span>
                  </div>
                  {/* Show recommendation preference for academy guidance */}
{formData.category_slug === 'academy-guidance' && (
  <div className="review-item">
    <span className="review-label">Recommendation Source</span>
    <span className="review-value">
      {formData.recommendation_preference === 'internal' 
        ? '🏠 Academy Programs Only' 
        : '🌐 Open Recommendations'}
    </span>
  </div>
)}
                  {formData.company_name && (
                    <div className="review-item">
                      <span className="review-label">Company</span>
                      <span className="review-value">{formData.company_name}</span>
                    </div>
                  )}

                  {formData.resume && (
                    <div className="review-item">
                      <span className="review-label">Resume</span>
                      <span className="review-value">✅ {formData.resume.original_name || 'Selected'}</span>
                    </div>
                  )}
                  {(formData.job_description_id || formData.job_description_text) && (
                    <div className="review-item">
                      <span className="review-label">Job Description</span>
                      <span className="review-value">✅ Provided</span>
                    </div>
                  )}
                  <div className="review-item">
                    <span className="review-label">Format</span>
                    <span className="review-value">
                      {formData.interview_type === 'text' ? '💬 Text Chat' :
                       formData.interview_type === 'voice' ? '🎙️ Web Voice' : '📞 Phone Call'}
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Duration</span>
                    <span className="review-value">{formData.duration} minutes</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Style</span>
                    <span className="review-value" style={{ textTransform: 'capitalize' }}>
                      {formData.interviewer_personality}
                    </span>
                  </div>
                </div>
              </div>

              {/* Coupon Code Section */}
              <div className="coupon-section">
                <h3>🎟️ Discount Code</h3>
                
                {!couponData ? (
                  // Coupon Input Form
                  <div className="coupon-input-row">
                    <input
                      type="text"
                      className="coupon-input"
                      placeholder="Enter code..."
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleApplyCoupon();
                        }
                      }}
                      disabled={couponLoading}
                    />
                    <button
                      type="button"
                      className="coupon-apply-btn"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                    >
                      {couponLoading ? (
                        <span className="coupon-spinner"></span>
                      ) : (
                        'Apply'
                      )}
                    </button>
                  </div>
                ) : (
                  // Coupon Applied Success
                  <div className="coupon-applied">
                    <div className="coupon-applied-info">
                      <span className="coupon-applied-icon">✅</span>
                      <div className="coupon-applied-details">
                        <span className="coupon-applied-code">{couponData.code}</span>
                        <span className="coupon-applied-savings">
                          {couponData.savings_display} (-{formatPrice(discountAmountInCents / 100)})
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="coupon-remove-btn"
                      onClick={handleRemoveCoupon}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Coupon Error */}
                {couponError && (
                  <div className="coupon-error">
                    <span>⚠️</span> {couponError}
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="review-pricing">
                {/* Subtotal */}
                <div className="pricing-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(currentPrice)}</span>
                </div>

                {/* Discount (only show if coupon applied) */}
                {couponData && (
                  <div className="pricing-row discount-row">
                    <span>Discount ({couponData.code})</span>
                    <span className="discount-amount">-{formatPrice(discountAmountInCents / 100)}</span>
                  </div>
                )}

                {/* Divider */}
                <div className="pricing-divider"></div>

                {/* Total */}
                <div className="pricing-row total-row">
                  <span>Total</span>
                  <span className="total-price">
                    {couponData?.is_free ? 'FREE' : formatPrice(finalPrice)}
                  </span>
                </div>

                {/* Free badge */}
                {couponData?.is_free && (
                  <div className="free-session-badge">
                    🎉 This session is free with your coupon!
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="book-session-page">
      {/* Progress */}
      <div className="steps-progress">
        {steps.map((stepName, index) => (
          <div
            key={index}
            className={`step-indicator ${index + 1 === step ? 'active' : ''} ${index + 1 < step ? 'completed' : ''}`}
          >
            <div className="step-number">{index + 1}</div>
            <span className="step-name">{stepName}</span>
          </div>
        ))}
      </div>

      {/* Errors */}
      {error && (
        <div className="error-banner">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* TOP Navigation - compact, no price */}
{step >= 1 && (
  <div className="step-navigation nav-top">
    <button className="btn btn-secondary" onClick={prevStep} disabled={step === 1}>
      ← Back
    </button>
    <div className="nav-spacer"></div>
    {step < totalSteps ? (
      <button className="btn btn-primary" onClick={nextStep} disabled={!validateStep()}>
        Continue →
      </button>
    ) : (
      <button
        className="btn btn-primary btn-checkout"
        onClick={handleCheckout}
        disabled={loading || !validateStep()}
      >
        {loading ? 'Processing...' : couponData?.is_free ? 'Complete Booking →' : `Pay ${formatPrice(finalPrice)} →`}
      </button>
    )}
  </div>
)}
 

      {/* Content */}
      <div className="step-container">{renderStepContent()}</div>

      {/* Navigation */}
      <div className="step-navigation">
        <button className="btn btn-secondary" onClick={prevStep} disabled={step === 1}>
          ← Back
        </button>

        <div className="price-display">
          <span className="price-label">
            {couponData ? 'Discounted Price' : 'Session Price'}
          </span>
          <span className={`price-value ${couponData ? 'discounted' : ''}`}>
            {steps.indexOf('Duration') < step ? (
              couponData?.is_free ? 'FREE' : formatPrice(finalPrice)
            ) : '—'}
          </span>
          {couponData && !couponData.is_free && (
            <span className="original-price">{formatPrice(currentPrice)}</span>
          )}
        </div>

        {step < totalSteps ? (
          <button className="btn btn-primary" onClick={nextStep} disabled={!validateStep()}>
            Continue →
          </button>
        ) : (
          <button
            className="btn btn-primary btn-checkout"
            onClick={handleCheckout}
            disabled={loading || !validateStep()}
          >
            {loading ? 'Processing...' : couponData?.is_free ? 'Complete Booking →' : `Pay ${formatPrice(finalPrice)} →`}
          </button>
        )}
      </div>
    </div>
  );
};

export default BookSession;